import { Users, Search, Plus, Filter, Trash2, Edit, Phone, PhoneCall, Copy, Calendar, MessageCircle, ChevronDown, Clock, Play, MoreVertical, ArrowLeft } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import AddMemberView from './AddMemberView';
import { createPortal } from 'react-dom';
import API_BASE_URL from '../../config';
import AddPlanView from './AddPlanView';
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import Extendplan from './Extendplan';
import RecordPaymentView from './RecordPaymentView';

const PAGE_SIZE = 10;

const Loader = () => createPortal(
  <div className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-lime-400"></div>
    <p className="text-white ml-4">Loading...</p>
  </div>,
  document.body
);


const formatToDDMMYYYY = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (isNaN(date)) return 'N/A';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const Banner = ({ type, message, onClose }) => {
  if (!type || !message) return null;

  const base = 'fixed top-4 left-1/2 transform -translate-x-1/2 w-96 max-w-[90vw] flex items-center justify-between px-6 py-4 rounded-2xl shadow-2xl border-2 z-[9999] animate-in slide-in-from-top-2 fade-in duration-300';
  const styles = type === 'success'
    ? 'bg-emerald-500/50 backdrop-blur-xl text-white border-emerald-400/50 shadow-emerald-500/25'
    : type === 'error'
      ? 'bg-red-500/50 backdrop-blur-xl text-white border-red-400/50 shadow-red-500/25'
      : 'bg-blue-500/50 backdrop-blur-xl text-white border-blue-400/50 shadow-blue-500/25';

  return (
    <div className={`${base} ${styles}`}>
      <span className="font-medium">{message}</span>
      <button
        onClick={onClose}
        className="ml-4 p-1 hover:bg-white/20 rounded-full transition-all hover:scale-110"
        aria-label="Close"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};


const IDCardModal = ({ member, onClose, gymName }) => {
  const getAvatarInitials = () => {
    return `${member.Firstname?.[0] || ''}${member.Lastname?.[0] || ''}`.toUpperCase() || 'JD';
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-[10000] w-full max-w-sm rounded-3xl overflow-hidden bg-gradient-to-b from-zinc-900 to-zinc-950 border border-lime-400/30 shadow-2xl p-6 flex flex-col items-center">
        {/* Background glow */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-lime-400/10 rounded-full filter blur-2xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/10 rounded-full filter blur-2xl" />

        {/* Card Header */}
        <div className="w-full flex justify-between items-center border-b border-[#2b2b2b] pb-4 mb-6">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-lime-400 flex items-center justify-center text-zinc-950 font-black text-xs">FS</div>
            <span className="font-extrabold text-white tracking-wider text-xs uppercase">{gymName || 'FitScan Gym'}</span>
          </div>
          <span className="text-[10px] bg-lime-400/20 text-lime-400 font-bold px-2 py-0.5 rounded border border-lime-400/30">MEMBER</span>
        </div>

        {/* Member Photo / Avatar */}
        <div className="relative mb-4">
          <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-lime-400 flex items-center justify-center bg-lime-400/10 text-lime-400 text-3xl font-bold shadow-lg shadow-lime-400/15">
            {member.PhotoUrl ? (
              <img
                src={member.PhotoUrl}
                alt={`${member.Firstname} ${member.Lastname}`}
                className="w-full h-full object-cover"
              />
            ) : (
              getAvatarInitials()
            )}
          </div>
        </div>

        {/* Member Name */}
        <h2 className="text-xl font-bold text-white mb-1">{member.Firstname} {member.Lastname}</h2>
        <p className="text-gray-400 text-xs mb-6">+{member.Phone}</p>

        {/* Details grid */}
        <div className="w-full grid grid-cols-2 gap-4 bg-zinc-900/50 border border-[#2b2b2b] rounded-2xl p-4 mb-6 text-xs">
          <div>
            <span className="text-gray-500 block uppercase tracking-wider text-[9px] mb-0.5">Member ID</span>
            <span className="font-bold text-white text-sm">#{member.dbId || member.id}</span>
          </div>
          <div>
            <span className="text-gray-500 block uppercase tracking-wider text-[9px] mb-0.5">Current Plan</span>
            <span className="font-bold text-lime-400 text-sm">{member.PlanName}</span>
          </div>
          <div>
            <span className="text-gray-500 block uppercase tracking-wider text-[9px] mb-0.5">Valid From</span>
            <span className="font-bold text-gray-300">{member.joinDate ? new Date(member.joinDate).toLocaleDateString('en-IN') : 'N/A'}</span>
          </div>
          <div>
            <span className="text-gray-500 block uppercase tracking-wider text-[9px] mb-0.5">Expires On</span>
            <span className={`font-bold ${new Date(member.ExpiryDate) < new Date() ? 'text-red-400' : 'text-gray-300'}`}>
              {member.ExpiryDate ? new Date(member.ExpiryDate).toLocaleDateString('en-IN') : 'N/A'}
            </span>
          </div>
        </div>

        {/* QR Code / Barcode simulation */}
        <div className="flex flex-col items-center gap-2 mb-6">
          <div className="bg-white p-2 rounded-xl">
            <div className="w-24 h-24 bg-zinc-950 flex items-center justify-center text-lime-400">
              <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="6" height="6" rx="1" />
                <rect x="2" y="16" width="6" height="6" rx="1" />
                <rect x="16" y="2" width="6" height="6" rx="1" />
                <path d="M16 16h2v2h-2zM18 18h2v2h-2zM20 16h2v2h-2zM16 20h2v2h-2zM18 20h2v-2h2v4h-4z" />
                <path d="M7 7h.01M7 17h.01M17 7h.01" />
              </svg>
            </div>
          </div>
          <span className="text-[10px] text-gray-500 tracking-widest font-mono">FS-{member.dbId || member.id}-{member.Phone?.slice(-4)}</span>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="w-full bg-[#222] hover:bg-[#2b2b2b] text-white py-2.5 rounded-xl font-bold text-sm transition-all border border-[#333]"
        >
          Close ID Card
        </button>
      </div>
    </div>
  );
};


const MemberCard = ({
  member,
  onAvatarClick,
  onExtend,
  onDelete,
  onSync,
  onBioEnroll,
  onIdCard,
  onEdit,
  onAddOn,
  onAddPay,
  onRenew
}) => {
  const getAvatarInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'JD';
  };

  const truncatePhone = (phone) => {
    if (!phone) return '';
    return `+91 ${phone.slice(-10)}`;
  };

  return (
    <div className="bg-[#181818] border border-[#2b2b2b] rounded-2xl p-4 hover:shadow-lg hover:border-lime-400/30 transition-all flex flex-col gap-4">
      {/* Top row: Avatar + details + badge */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Avatar - Squircle for photo, circle for initials */}
          <div className={`w-14 h-14 overflow-hidden flex items-center justify-center relative flex-shrink-0 ${member.PhotoUrl ? 'rounded-2xl' : 'rounded-full bg-lime-400/10 text-lime-400'
            }`}>
            {member.PhotoUrl ? (
              <img
                src={member.PhotoUrl}
                alt={`${member.Firstname} ${member.Lastname}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="text-base font-bold">
                {getAvatarInitials(member.Firstname, member.Lastname)}
              </div>
            )}
          </div>

          {/* Name, branch, phone */}
          <div className="flex flex-col min-w-0">
            <h3 className="font-bold text-white text-base leading-snug">{member?.Firstname?.toLowerCase()} {member?.Lastname?.toLowerCase()}</h3>
            <div className="flex items-center gap-1 text-gray-400 text-xs mt-0.5">
              <svg className="w-3.5 h-3.5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span>Amp</span>
            </div>
            <div className="flex items-center gap-1.5 text-blue-400 text-xs mt-0.5 font-medium">
              <svg className="w-3.5 h-3.5 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.5 19.5 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
              </svg>
              <span>{truncatePhone(member?.Phone)}</span>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(member?.Phone);
                }}
                className="hover:scale-110 transition-transform p-0.5 rounded hover:bg-zinc-800"
              >
                <svg className="w-3.5 h-3.5 text-gray-500 hover:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Badge (#86) */}
        <div className="bg-[#0b2545] text-[#8da9c4] border border-[#134074] rounded-lg px-3 py-1 font-bold text-xs">
          #{member.dbId || member.id}
        </div>
      </div>

      {/* Gray box section with details */}
      <div className="bg-[#121212] border border-[#2b2b2b] rounded-2xl p-4 flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-[10px] text-gray-500 uppercase tracking-wider block mb-0.5">Membership</span>
            <span className="text-xs font-bold text-gray-300">
              {member.joinDate ? new Date(member.joinDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'} - {member.ExpiryDate ? new Date(member.ExpiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
            </span>
          </div>
          <div>
            <span className="text-[10px] text-gray-500 uppercase tracking-wider block mb-0.5">Plan</span>
            <span className="text-xs font-bold text-gray-300">{member.PlanName?.toLowerCase()}</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 border-t border-[#2b2b2b] pt-3">
          <div>
            <span className="text-[10px] text-gray-500 uppercase tracking-wider block mb-0.5">Total</span>
            <span className="text-sm font-bold text-gray-300">₹{member.totalAmount}</span>
          </div>
          <div>
            <span className="text-[10px] text-gray-500 uppercase tracking-wider block mb-0.5">Paid</span>
            <span className="text-sm font-bold text-gray-300">₹{member.paidAmount}</span>
          </div>
          <div>
            <span className="text-[10px] text-gray-500 uppercase tracking-wider block mb-0.5">Due</span>
            <span className={`text-sm font-bold ${member.dueAmount > 0 ? 'text-red-400' : 'text-gray-300'}`}>
              ₹{member.dueAmount}
            </span>
          </div>
        </div>
      </div>

      {/* Bottom Actions Row (Horizontal Scroll) */}
      <div className="flex items-center gap-4 overflow-x-auto pb-1 px-1 scrollbar-none">
        {[
          { label: 'Profile', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="4" /><path d="M16 8v8M8 8v8" /></svg>, action: onAvatarClick },
          { label: 'Sync', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 11-.57-8.38l5.67-5.67" /></svg>, action: onSync },
          { label: 'Bio Enroll', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a10 10 0 00-7.3 16.82l.46-.22a8 8 0 1113.68 0l.46.22A10 10 0 0012 2zM12 6a6 6 0 00-6 6v1a1 1 0 002 0v-1a4 4 0 118 0v1a1 1 0 002 0v-1a6 6 0 00-6-6z" /></svg>, action: onBioEnroll },
          { label: 'ID-Card', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="M15 8h2M15 12h2M15 16h2M6 16c0-1.5 1-3 3-3s3 1.5 3 3" /></svg>, action: onIdCard },
          { label: 'Edit', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>, action: onEdit },
          { label: 'Add On', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 8v8M8 12h8" /></svg>, action: onAddOn },
          { label: 'Add Pay', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>, action: onAddPay },
          { label: 'Renew', icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.5 2v6h-6M21.34 15.57a10 10 0 11-.57-8.38l5.67-5.67" /></svg>, action: onRenew },
        ].map((item, idx) => (
          <button
            key={idx}
            onClick={item.action}
            className="flex flex-col items-center gap-1 min-w-[56px] text-gray-400 hover:text-white transition-colors"
          >
            <div className="w-9 h-9 rounded-full bg-[#222] border border-[#2b2b2b] flex items-center justify-center hover:bg-lime-400 hover:text-zinc-950 transition-colors">
              {item.icon}
            </div>
            <span className="text-[10px] font-medium tracking-tight whitespace-nowrap">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const MembersTab = ({ expiryDays = 7, onBack }) => {
  const userInfo = JSON.parse(localStorage.getItem('fitscan_user') || '{}');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [members, setMembers] = useState([]);
  const [expiringSoonMembers, setExpiringSoonMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('monthly');
  const [graphData, setGraphData] = useState([]);
  const [extendMember, setExtendMember] = useState(null);
  const [banner, setBanner] = useState({ type: null, message: '' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showManualFreezeModal, setShowManualFreezeModal] = useState(false);
  const [manualFreezeDuration, setManualFreezeDuration] = useState(7);
  const [manualFreezeReason, setManualFreezeReason] = useState('');
  const [manualFreezeMember, setManualFreezeMember] = useState(null);
  const [actioningFreeze, setActioningFreeze] = useState(false);
  const [memberHistory, setMemberHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [error, setError] = useState(''); // eslint-disable-line no-unused-vars
  const [page, setPage] = useState(0);

  const [trainers, setTrainers] = useState([]); // eslint-disable-line no-unused-vars
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState(location.state?.activeFilter || 'all');
  const [showIDCard, setShowIDCard] = useState(false);
  const [idCardMember, setIdCardMember] = useState(null);
  const [activeDropdownMember, setActiveDropdownMember] = useState(null);
  const [showAddPay, setShowAddPay] = useState(false);
  const [selectedPayMember, setSelectedPayMember] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const planOptions = Array.from(new Set(members.map(m => m.PlanName || '').filter(Boolean)));

  useEffect(() => {
    if (location.state?.activeFilter) {
      setActiveFilter(location.state.activeFilter);
      setPage(0);
    }
  }, [location.state?.activeFilter]);

  const fetchTrainers = useCallback(async () => {
    const userInfo = JSON.parse(localStorage.getItem('fitscan_user') || '{}');
    try {
      const res = await fetch(`${API_BASE_URL}/api/gym/GetTrainers`, {
        method: 'POST', // or GET if your API is GET
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        },
        body: JSON.stringify(userInfo.gymcode) // remove if GET without body
      });

      if (!res.ok) throw new Error('Failed to load trainers');
      const data = await res.json();

      const mapped = data.map(t => ({
        id: t.id,
        name: t.fullName,        // ✅ Use fullName from API
        username: t.email,        // ✅ Use email as username fallback
        email: t.email,
        phoneNumber: t.phoneNumber
      }));

      setTrainers(mapped);
    } catch (err) {
      console.error(err);
      setBanner({ type: 'error', message: 'Failed to load trainers' });
    }
  }, []);

  const fetchGymUsers = useCallback(async () => {
    const userInfo = JSON.parse(localStorage.getItem('fitscan_user') || '{}');
    const tokenObj = JSON.parse(localStorage.getItem('accessToken') || '{}');

    if (!userInfo.gymcode) {
      setBanner({ type: 'error', message: 'Gym code is missing. Please log in again.' });
      return;
    }

    if (!tokenObj.token) {
      setBanner({ type: 'error', message: 'Access token is missing. Please log in again.' });
      return;
    }

    try {
      setLoading(true);

      const primaryUrl = `${API_BASE_URL}/api/gym/Gymuserdata`;
      const response = await fetch(primaryUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenObj.token}`
        },
        body: JSON.stringify({ gymcode: userInfo.gymcode, expiryDays: expiryDays }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch gym users. Status: ${response.status}`);
      }

      const data = await response.json();

      const allUsers = (data.allGymUsers || []).map((u, idx) => {
        return {
          id: idx + 1,
          dbId: u.id,
          username: u.username,
          Firstname: u.firstName,
          Lastname: u.lastName,
          Phone: u.phoneNumber,
          PhotoUrl: u.photoUrl || null,
          PlanName: u.userplanname || 'Basic',
          joinDate: u.userPlanJoiningDate?.split('T')[0],
          ExpiryDate: u.userPlanExpiryDate?.split('T')[0],
          TrainerId: u.trainerId || null,
          TrainerName: u.trainerFullName || null,
          TrainerUsername: u.trainerUsername || null,
          Status: u.status || 'Active',
          IsFrozen: u.isFrozen,
          ActiveFreezeStartDate: u.activeFreezeStartDate?.split('T')[0],
          ActiveFreezeEndDate: u.activeFreezeEndDate?.split('T')[0],
          lifetimeCheckins: u.lifetimeCheckins || 0,
          totalAmount: u.totalAmount || 0,
          paidAmount: u.paidAmount || 0,
          dueAmount: u.dueAmount || 0,
        };
      });

      const expiring = (data.expiringSoon || []).map((u, idx) => {
        return {
          id: idx + 1,
          dbId: u.id,
          username: u.username,
          Firstname: u.firstName,
          Lastname: u.lastName,
          Phone: u.phoneNumber,
          PhotoUrl: u.photoUrl || null,
          PlanName: u.userplanname || 'Basic',
          joinDate: u.userPlanJoiningDate?.split('T')[0],
          ExpiryDate: u.userPlanExpiryDate?.split('T')[0],
          TrainerId: u.trainerId || null,
          TrainerName: u.trainerFullName || null,
          TrainerUsername: u.trainerUsername || null,
          Status: u.status || 'Active',
          IsFrozen: u.isFrozen,
          ActiveFreezeStartDate: u.activeFreezeStartDate?.split('T')[0],
          ActiveFreezeEndDate: u.activeFreezeEndDate?.split('T')[0],
          lifetimeCheckins: u.lifetimeCheckins || 0,
          totalAmount: u.totalAmount || 0,
          paidAmount: u.paidAmount || 0,
          dueAmount: u.dueAmount || 0,
        };
      });

      setMembers(allUsers);
      setExpiringSoonMembers(expiring);
      setLoading(false);
      setError('');
      setPage(0);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch gym users');
    } finally {
      setLoading(false);
    }
  }, [expiryDays]);

  const handleManualUnfreeze = async (memberUsername) => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/membershipfreeze/manual-unfreeze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          memberUsername,
          gymCode: userInfo.gymcode
        })
      });

      if (response.ok) {
        setBanner({ type: 'success', message: 'Member unfrozen successfully!' });
        await fetchGymUsers();
      } else {
        const errText = await response.text();
        setBanner({ type: 'error', message: errText || 'Failed to unfreeze member.' });
      }
    } catch (err) {
      console.error(err);
      setBanner({ type: 'error', message: 'An error occurred during unfreezing.' });
    } finally {
      setLoading(false);
    }
  };

  const handleManualFreeze = async () => {
    if (!manualFreezeDuration || manualFreezeDuration <= 0) {
      setBanner({ type: 'error', message: 'Duration must be positive.' });
      return;
    }
    setActioningFreeze(true);
    try {
      const token = JSON.parse(localStorage.getItem('accessToken') || '{}').token;
      const response = await fetch(`${API_BASE_URL}/api/membershipfreeze/manual-freeze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          memberUsername: manualFreezeMember.username,
          gymCode: userInfo.gymcode,
          startDate: new Date().toISOString().split('T')[0],
          durationDays: Number(manualFreezeDuration),
          reason: manualFreezeReason || 'Manual freeze by gym staff'
        })
      });

      if (response.ok) {
        setBanner({ type: 'success', message: 'Member frozen successfully!' });
        setShowManualFreezeModal(false);
        setManualFreezeReason('');
        setManualFreezeDuration(7);
        await fetchGymUsers();
      } else {
        const errText = await response.text();
        setBanner({ type: 'error', message: errText || 'Failed to freeze member.' });
      }
    } catch (err) {
      console.error(err);
      setBanner({ type: 'error', message: 'An error occurred during freezing.' });
    } finally {
      setActioningFreeze(false);
    }
  };


  useEffect(() => {
    fetchTrainers();
    fetchGymUsers();
  }, [fetchTrainers, fetchGymUsers]);




  useEffect(() => {
    if (!selectedMember) {
      setMemberHistory([]);
      setIsHistoryExpanded(false);
      setShowAllHistory(false);
      return;
    }
    setShowAllHistory(false);

    const fetchMemberHistory = async () => {
      setIsHistoryLoading(true);
      try {
        const userInfo = JSON.parse(localStorage.getItem('fitscan_user') || '{}');
        const tokenObj = JSON.parse(localStorage.getItem('accessToken') || '{}');
        const res = await fetch(`${API_BASE_URL}/api/gym/member/${selectedMember.username}/history?gymcode=${userInfo.gymcode}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokenObj.token || ''}`
          }
        });
        if (res.ok) {
          const data = await res.json();
          setMemberHistory(data);
        } else {
          console.error("Failed to load member history:", res.statusText);
        }
      } catch (err) {
        console.error("Error loading member history:", err);
      } finally {
        setIsHistoryLoading(false);
      }
    };

    fetchMemberHistory();
  }, [selectedMember]);

  useEffect(() => {
    if (!banner.type) return;
    const t = setTimeout(() => setBanner({ type: null, message: '' }), 4000);
    return () => clearTimeout(t);
  }, [banner]);

  const confirmDeleteUser = (username) => {
    setUserToDelete(username);
    setDeleteConfirmOpen(true);
  };

  useEffect(() => {
    if (!members.length) {
      setGraphData([]);
      return;
    }

    const grouped = {};
    members.forEach((member) => {
      const date = new Date(member.joinDate);
      let key = '';

      if (timeFilter === 'weekly') {
        const weekStart = new Date(date.setDate(date.getDate() - date.getDay()));
        key = weekStart.toISOString().split('T')[0];
      } else if (timeFilter === 'monthly') {
        key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      } else if (timeFilter === 'yearly') {
        key = `${date.getFullYear()}`;
      }

      grouped[key] = (grouped[key] || 0) + 1;
    });

    const result = Object.entries(grouped)
      .map(([key, count]) => ({
        time: key,
        users: count,
      }))
      .sort((a, b) => a.time.localeCompare(b.time));

    setGraphData(result);
  }, [members, timeFilter]);

  const formatXAxis = (tickItem) => {
    if (timeFilter === 'monthly' && tickItem.includes('-')) {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const [year, month] = tickItem.split('-');
      const monthIndex = parseInt(month, 10) - 1;

      if (monthIndex >= 0 && monthIndex < 12) {
        return `${year}-${monthNames[monthIndex]}`;
      }
    }
    return tickItem;
  };

  const handleCopyPhone = async (phone) => {
    try {
      await navigator.clipboard.writeText(phone);
      setBanner({ type: 'success', message: `Copied: ${phone}` });
    } catch (err) {
      console.error('Failed to copy phone:', err);
      setBanner({ type: 'error', message: 'Failed to copy number' });
    }
  };

  const handleCloseMemberPopup = () => {
    setSelectedMember(null);
  };

  const getAvatarInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'JD';
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return 'gray';
    const expiry = new Date(expiryDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (diffDays <= 7 && diffDays >= 0) return 'yellow';
    if (expiry < today) return 'red';
    return 'lime';
  };


  const filteredMembers = members.filter(member => {
    // Status chips filter (all/active/inactive based on status)
    if (activeFilter !== 'all') {
      if (activeFilter === 'active' && member.Status !== 'Active') return false;
      if (activeFilter === 'inactive' && member.Status === 'Active') return false;
    }

    // Date range filters
    if (filterFrom && (!member.joinDate || member.joinDate < filterFrom)) return false;
    if (filterTo && (!member.joinDate || member.joinDate > filterTo)) return false;

    // Plan filter
    if (filterPlan && member.PlanName !== filterPlan) return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        (member.Firstname && member.Firstname.toLowerCase().includes(query)) ||
        (member.Lastname && member.Lastname.toLowerCase().includes(query)) ||
        (member.Phone && member.Phone.toLowerCase().includes(query)) ||
        (member.username && member.username.toLowerCase().includes(query)) ||
        (member.PlanName && member.PlanName.toLowerCase().includes(query))
      );
    }
    return true;
  });



  const updateMemberTrainer = async (memberUsername, trainerId) => {
    try {
      const trainer = trainerId ? members.find(m => m.TrainerId === trainerId) : null;

      const res = await fetch(`${API_BASE_URL}/api/gym/AssignTrainer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        },
        body: JSON.stringify({
          username: memberUsername,
          trainerUsername: trainer?.TrainerUsername || null,  // ✅ Send trainer email
          gymCode: JSON.parse(localStorage.getItem('fitscan_user') || '{}').gymcode
        }),
      });

      if (!res.ok) throw new Error('Failed to update trainer');
      setBanner({ type: 'success', message: 'Trainer details updated successfully.' });

      // ✅ OPTIMISTIC: Update selectedMember IMMEDIATELY
      setSelectedMember(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          TrainerId: trainerId || null,
          TrainerName: trainer?.TrainerName || null,
          TrainerEmail: trainer?.TrainerUsername || null
        };
      });

      await fetchGymUsers();
    } catch (err) {
      console.error(err);
      setBanner({ type: 'error', message: 'Failed to assign trainer.' });
      await fetchGymUsers();  // Revert on error
    }
  };

  const total = filteredMembers.length;
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const pagedMembers = filteredMembers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleDelete = async (username) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/gym/${username}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        }
      });
      if (!res.ok) throw new Error('Failed to delete user');
      await fetchGymUsers();
      setBanner({ type: 'success', message: 'User deleted successfully.' });
    } catch (err) {
      console.error(err);
      setBanner({ type: 'error', message: 'Error deleting user. Please try again.' });
    } finally {
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    }
  };

  // Filters Modal (exactly like payments tab)
  const FiltersModal = () => {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center"
        aria-modal="true"
        tabIndex={-1}
        onClick={() => setShowFilters(false)}
      >
        <div
          className="bg-[#181818] text-gray-200 p-6 rounded-xl w-[90vw] max-w-md z-60 shadow-lg relative"
          onClick={e => e.stopPropagation()}
        >
          <button
            className="absolute right-2 top-2 text-gray-400 hover:text-red-400"
            onClick={() => setShowFilters(false)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Filter className="w-5 h-5 text-lime-400" />
            Advanced Filters
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-gray-300">Join Date From</label>
              <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
                className="w-full px-3 py-2 bg-[#222] rounded border border-[#2b2b2b]" />
            </div>
            <div>
              <label className="block mb-1 text-gray-300">Join Date To</label>
              <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
                className="w-full px-3 py-2 bg-[#222] rounded border border-[#2b2b2b]" />
            </div>
            <div>
              <label className="block mb-1 text-gray-300">Plan</label>
              <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)}
                className="w-full px-3 py-2 bg-[#222] rounded border border-[#2b2b2b]">
                <option value="">All Plans</option>
                {planOptions.map(plan =>
                  <option key={plan} value={plan}>{plan}</option>
                )}
              </select>
            </div>
          </div>
          <div className="flex justify-end mt-6 gap-2">
            <button
              className="px-4 py-2 rounded bg-[#222] border border-[#2b2b2b] text-gray-400 hover:bg-[#2b2b2b]"
              onClick={() => {
                setFilterFrom('');
                setFilterTo('');
                setFilterPlan('');
                setShowFilters(false);
                setPage(0);
              }}
            >Reset</button>
            <button
              className="px-4 py-2 rounded bg-lime-400 text-[#181f10] hover:bg-lime-300 font-semibold"
              onClick={() => {
                setShowFilters(false);
                setPage(0);
              }}
            >Apply</button>
          </div>
        </div>
      </div>
    );
  };




  return (
    <div className="space-y-6">

      {loading && <Loader />}
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: null, message: '' })}
      />

      {/* Mobile Back Header */}
      {onBack && (
        <div className="md:hidden flex items-center justify-between border-b border-[#2b2b2b] pb-4">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white transition-all">
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-xl font-bold text-white uppercase tracking-wider">Members</h1>
          </div>
          <div className="flex items-center gap-4 text-gray-400">
            <button
              onClick={() => setShowMobileSearch(!showMobileSearch)}
              className="p-1 hover:text-white transition-colors"
            >
              <Search size={20} />
            </button>
            <button
              onClick={() => setShowFilters(true)}
              className="p-1 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
            </button>
            <button
              onClick={() => setShowAddMember(true)}
              className="p-1 hover:text-white transition-colors text-lime-400"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Mobile Search Input */}
      {onBack && showMobileSearch && (
        <div className="md:hidden relative w-full mb-4">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            className="w-full pl-10 pr-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400 text-white text-sm"
          />
        </div>
      )}

      {/* Desktop/Default Header */}
      <div className={`${onBack ? 'hidden md:flex' : 'flex'} flex-col md:flex-row justify-between items-start md:items-center gap-4`}>
        <h1 className="text-2xl font-bold flex items-center gap-2 mb-2 md:mb-0">
          <Users size={24} className="text-lime-400" />
          Member Management
        </h1>

        <div className="flex flex-col md:flex-row items-start md:items-center gap-3 w-full md:w-auto">
          {/* Search + Filter (same line like payments tab) */}
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(0);
                }}
                className="w-full pl-10 pr-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>

            <button
              className="flex items-center gap-2 bg-[#222] border border-[#2b2b2b] hover:bg-[#2b2b2b] px-3 py-2 rounded-lg whitespace-nowrap"
              onClick={() => setShowFilters(true)}
            >
              <Filter size={18} />
              <span>Filters</span>
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button onClick={() => setShowAddPlan(true)} className="flex items-center gap-2 bg-lime-400 hover:bg-lime-300 text-[#181f10] px-4 py-2 rounded-lg justify-center flex-1 sm:flex-none">
              <Plus size={18} />
              <span>Manage Plans</span>
            </button>
            <button onClick={() => setShowAddMember(true)} className="flex items-center gap-2 bg-lime-400 hover:bg-lime-300 text-[#181f10] px-4 py-2 rounded-lg justify-center flex-1 sm:flex-none">
              <Plus size={18} />
              <span>Add Member</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters: Cards on mobile, Chips on desktop */}
      <div className="block md:hidden">
        <div className="grid grid-cols-3 gap-3 mb-2">
          {[
            { id: 'all', label: 'Total Memberships', count: members.length, color: 'text-blue-400' },
            { id: 'active', label: 'Live Memberships', count: members.filter(m => m.Status === 'Active').length, color: 'text-lime-400' },
            { id: 'inactive', label: 'Expired Memberships', count: members.filter(m => m.Status !== 'Active').length, color: 'text-red-400' }
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => {
                setActiveFilter(filter.id);
                setPage(0);
              }}
              className={`flex flex-col items-center justify-center p-3 rounded-2xl border text-center transition-all ${activeFilter === filter.id
                ? 'bg-lime-500/10 border-lime-400'
                : 'bg-[#181818] border-[#2b2b2b] hover:border-[#333]'
                }`}
            >
              <span className="text-[9px] font-bold text-gray-400 mb-1 leading-tight">{filter.label}</span>
              <span className={`text-xl font-bold ${activeFilter === filter.id ? 'text-lime-400' : 'text-white'}`}>{filter.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="hidden md:flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'all', label: 'Total' },
          { id: 'active', label: 'Live' },
          { id: 'inactive', label: 'Expired' }
        ].map((filter) => (
          <button
            key={filter.id}
            onClick={() => {
              setActiveFilter(filter.id);
              setPage(0);
            }}
            className={`px-4 py-2 rounded-full capitalize whitespace-nowrap ${activeFilter === filter.id
              ? 'bg-lime-400 text-[#181f10] font-bold'
              : 'bg-[#222] text-gray-400 hover:bg-[#2b2b2b]'
              }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {showFilters && <FiltersModal />}

      {/* Mobile Card List (no surrounding container, scrollable naturally) */}
      <div className="block md:hidden space-y-4">
        {pagedMembers.length === 0 ? (
          <div className="p-16 text-center text-gray-400 bg-[#181818] border border-[#2b2b2b] rounded-2xl">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-500" />
            <div>No members found matching your search.</div>
            <div className="text-sm mt-1">Try adjusting your filters or search terms.</div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {pagedMembers.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                onAvatarClick={() => setSelectedMember(member)}
                onExtend={() => setExtendMember(member)}
                onDelete={() => confirmDeleteUser(member.username)}
                onSync={() => setBanner({ type: 'success', message: 'Sync completed successfully!' })}
                onBioEnroll={() => setBanner({ type: 'success', message: `Biometric enrollment started for ${member.Firstname}!` })}
                onIdCard={() => {
                  setIdCardMember(member);
                  setShowIDCard(true);
                }}
                onEdit={() => setBanner({ type: 'info', message: 'Edit user details functionality is coming soon!' })}
                onAddOn={() => setBanner({ type: 'info', message: 'Add-on services configuration coming soon!' })}
                onAddPay={() => {
                  setSelectedPayMember(member);
                  setShowAddPay(true);
                }}
                onRenew={() => setExtendMember(member)}
              />
            ))}

            {/* Mobile Pagination */}
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between mt-4 px-2 py-3 bg-[#181818] border border-[#2b2b2b] rounded-2xl">
                <button
                  className="flex items-center gap-1 px-3 py-1.5 bg-[#222] hover:bg-[#2b2b2b] border border-[#2b2b2b] rounded-xl text-xs font-semibold text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                <div className="text-xs text-gray-400">
                  Page <span className="font-semibold text-white">{page + 1}</span>/<span className="font-semibold text-lime-400">{Math.ceil(total / PAGE_SIZE)}</span>
                </div>
                <button
                  className="flex items-center gap-1 px-3 py-1.5 bg-lime-400 hover:bg-lime-300 text-[#181f10] rounded-xl text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  onClick={() => setPage(p => Math.min(maxPage, p + 1))}
                  disabled={page >= maxPage}
                >
                  Next
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Desktop Container (big box with table) */}
      <div className="hidden md:block bg-[#181818] border border-[#2b2b2b] rounded-xl">
        <div className="p-6 pb-4">
          {/* Header - Title Only */}
          <h3 className="text-lg font-semibold text-white mb-6">All Members ({total})</h3>

          {/* Member Desktop layout: HTML Table */}
          <div className="overflow-x-auto max-h-[50vh]">
            <table className="w-full text-left border-collapse min-w-[1200px]">
              <thead>
                <tr className="bg-[#222] border-b border-[#2b2b2b] text-gray-300 font-semibold text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 w-16">Photo</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Rank Badge</th>
                  <th className="px-4 py-3">Membership Dates</th>
                  <th className="px-4 py-3">Plan Name</th>
                  <th className="px-4 py-3">Total Amount</th>
                  <th className="px-4 py-3">Paid Amount</th>
                  <th className="px-4 py-3">Due Amount</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2b2b2b] text-xs text-gray-200">
                {pagedMembers.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-12 text-center text-gray-500 bg-[#1a1a1a]">
                      No members found matching your search.
                    </td>
                  </tr>
                ) : (
                  pagedMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-[#222]/50 transition-colors">
                      <td className="px-4 py-2">
                        <div className="w-9 h-9 rounded-full bg-lime-400/10 flex items-center justify-center text-lime-400 relative">
                          {member.PhotoUrl ? (
                            <img
                              src={member.PhotoUrl}
                              alt={`${member.Firstname} ${member.Lastname}`}
                              className="w-full h-full object-cover rounded-full"
                            />
                          ) : (
                            <div className="font-bold">{member.Firstname?.[0]}{member.Lastname?.[0]}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-white">
                        {member.Firstname} {member.Lastname}
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {member.BranchName || 'Amp'}
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-300">
                        +{member.Phone}
                      </td>
                      <td className="px-4 py-3">
                        <span className="bg-[#0b2545] text-[#8da9c4] border border-[#134074] px-2.5 py-0.5 rounded font-bold">
                          #{member.dbId || member.id}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {formatToDDMMYYYY(member.joinDate)} - {formatToDDMMYYYY(member.ExpiryDate)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="bg-lime-500/10 text-lime-400 border border-lime-500/20 px-2.5 py-0.5 rounded font-medium">
                          {member.PlanName}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        ₹{member.totalAmount?.toLocaleString() || '0'}
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        ₹{member.paidAmount?.toLocaleString() || '0'}
                      </td>
                      <td className="px-4 py-3 font-semibold text-red-400">
                        ₹{member.dueAmount?.toLocaleString() || '0'}
                      </td>
                      <td className="px-4 py-3 text-right relative">
                        <button
                          onClick={() => setActiveDropdownMember(activeDropdownMember === member.id ? null : member.id)}
                          className="p-1.5 hover:bg-[#2b2b2b] rounded-lg text-gray-400 hover:text-white cursor-pointer"
                        >
                          <MoreVertical size={16} />
                        </button>

                        {activeDropdownMember === member.id && (
                          <div className="absolute right-4 mt-1 w-44 rounded-xl bg-zinc-900 border border-[#2b2b2b] shadow-2xl z-50 text-left overflow-hidden py-1">
                            <button
                              onClick={() => {
                                setSelectedMember(member);
                                setActiveDropdownMember(null);
                              }}
                              className="w-full px-4 py-2 hover:bg-[#222] text-white flex items-center gap-2 text-left cursor-pointer"
                            >
                              Profile
                            </button>
                            <button
                              onClick={() => {
                                setBanner({ type: 'success', message: 'Sync completed successfully!' });
                                setActiveDropdownMember(null);
                              }}
                              className="w-full px-4 py-2 hover:bg-[#222] text-white flex items-center gap-2 text-left cursor-pointer"
                            >
                              Sync
                            </button>
                            <button
                              onClick={() => {
                                setBanner({ type: 'success', message: `Biometric enrollment started for ${member.Firstname}!` });
                                setActiveDropdownMember(null);
                              }}
                              className="w-full px-4 py-2 hover:bg-[#222] text-white flex items-center gap-2 text-left cursor-pointer"
                            >
                              Bio Enroll
                            </button>
                            <button
                              onClick={() => {
                                setIdCardMember(member);
                                setShowIDCard(true);
                                setActiveDropdownMember(null);
                              }}
                              className="w-full px-4 py-2 hover:bg-[#222] text-white flex items-center gap-2 text-left cursor-pointer"
                            >
                              ID-Card
                            </button>
                            <button
                              onClick={() => {
                                setBanner({ type: 'info', message: 'Edit user details functionality is coming soon!' });
                                setActiveDropdownMember(null);
                              }}
                              className="w-full px-4 py-2 hover:bg-[#222] text-white flex items-center gap-2 text-left cursor-pointer"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                setBanner({ type: 'info', message: 'Add-on services configuration coming soon!' });
                                setActiveDropdownMember(null);
                              }}
                              className="w-full px-4 py-2 hover:bg-[#222] text-white flex items-center gap-2 text-left cursor-pointer"
                            >
                              Add On
                            </button>
                            <button
                              onClick={() => {
                                setSelectedPayMember(member);
                                setShowAddPay(true);
                                setActiveDropdownMember(null);
                              }}
                              className="w-full px-4 py-2 hover:bg-[#222] text-white flex items-center gap-2 text-left cursor-pointer"
                            >
                              Add Pay
                            </button>
                            <button
                              onClick={() => {
                                setExtendMember(member);
                                setActiveDropdownMember(null);
                              }}
                              className="w-full px-4 py-2 hover:bg-[#222] text-white flex items-center gap-2 text-left cursor-pointer"
                            >
                              Renew
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Page Info */}
          {total > 0 && (
            <div className="pt-6 border-t border-[#2b2b2b] text-xs text-gray-400 text-center bg-[#1a1a1a]/50 px-4 py-3 rounded-b-xl -mx-6 mt-2">
              Showing {((page * PAGE_SIZE) + 1)} to {Math.min((page + 1) * PAGE_SIZE, total)} of {total} members
              {total > PAGE_SIZE && ` • Page ${page + 1} of ${Math.ceil(total / PAGE_SIZE)}`}
            </div>
          )}
        </div>

        {/* 🆕 PAGINATION AT BOTTOM */}
        {total > PAGE_SIZE && (
          <div className="border-t border-[#2b2b2b] bg-[#1a1a1a]/50 px-6 py-4">
            <div className="flex items-center justify-center gap-2">
              <button
                className="flex items-center gap-1 px-3 py-1.5 bg-[#2b2b2b] hover:bg-[#333] border border-[#3b3b3b] rounded-lg text-xs font-medium text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>

              <div className="text-xs text-gray-400 px-3 whitespace-nowrap">
                Page <span className="font-semibold text-white">{page + 1}</span>/<span className="font-semibold text-lime-400">{Math.ceil(total / PAGE_SIZE)}</span>
              </div>

              <button
                className="flex items-center gap-1 px-3 py-1.5 bg-lime-400 hover:bg-lime-300 text-[#181f10] rounded-lg text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                onClick={() => setPage(p => Math.min(maxPage, p + 1))}
                disabled={page >= maxPage}
              >
                Next
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Expiring Soon Section */}
      {!onBack && expiringSoonMembers.length > 0 && (
        <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl overflow-hidden">
          <h2 className="text-lg font-semibold p-6 text-yellow-400 border-b border-[#2b2b2b]">
            🚨 Expiring in Next {expiryDays} Day{expiryDays > 1 ? 's' : ''} ({expiringSoonMembers.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6 max-h-96 overflow-y-auto">
            {expiringSoonMembers.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                onAvatarClick={() => setSelectedMember(member)}
                onExtend={() => setExtendMember(member)}
                onDelete={() => confirmDeleteUser(member.username)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Graph Section */}
      {!onBack && (
        <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-white">User Join Trends</h2>
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className="bg-[#222] text-white border border-[#2b2b2b] rounded px-2 py-1"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          {graphData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={graphData}
                barSize={Math.max(20, Math.min(60, Math.floor(300 / (graphData.length || 1))))}
                barCategoryGap="30%"
                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
              >
                <CartesianGrid stroke="#2b2b2b" />
                <XAxis
                  dataKey="time"
                  stroke="#aaa"
                  tickFormatter={formatXAxis}
                  tick={{ fontSize: 11 }}
                  padding={{ left: 20, right: 20 }}
                />
                <YAxis stroke="#aaa" allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#222', borderColor: '#2b2b2b', color: '#fff' }}
                />
                <Bar dataKey="users" fill="#84cc16" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-400 text-sm">No member data available for selected filter.</div>
          )}
        </div>
      )}

      {/* ✅ CHANGE 2: REDUCED HEIGHT POPUP BUTTONS */}
      {selectedMember && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={handleCloseMemberPopup}
          />
          <div className="relative z-10 w-full max-w-md bg-[#181818] border border-[#2b2b2b] rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-[#2b2b2b]">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-lime-400/10 flex items-center justify-center text-lime-400 relative flex-shrink-0">
                  {selectedMember.PhotoUrl ? (
                    <img
                      src={selectedMember.PhotoUrl}
                      alt={`${selectedMember.Firstname} ${selectedMember.Lastname}`}
                      className="w-full h-full object-cover rounded-full"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-lg font-bold text-lime-400">
                      {getAvatarInitials(selectedMember.Firstname, selectedMember.Lastname)}
                    </div>
                  )}

                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white truncate">
                    {selectedMember.Firstname} {selectedMember.Lastname}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-400">{selectedMember.username}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${selectedMember.IsFrozen
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : selectedMember.Status === 'Pending'
                        ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                        : selectedMember.Status === 'Expired'
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      }`}>
                      {selectedMember.IsFrozen ? 'Frozen' : (selectedMember.Status || 'Active')}
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleCloseMemberPopup}
                  className="p-2 hover:bg-[#2b2b2b] rounded-lg -m-2"
                >
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Phone - Primary Action */}
              <div className="flex items-center gap-3 p-4 bg-[#222] rounded-xl">
                {/* slightly smaller icon block */}
                <div className="w-10 h-10 bg-lime-400/10 rounded-xl flex items-center justify-center">
                  <Phone size={20} className="text-lime-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-400">Phone</p>
                  {/* full number, no truncate, slightly smaller to fit */}
                  <p className="text-base font-semibold text-white">
                    {selectedMember.Phone}
                  </p>
                </div>
                {/* make action icons a bit smaller so text has room */}
                <div className="flex gap-1.5">
                  <button
                    onClick={() => handleCopyPhone(selectedMember.Phone)}
                    className="p-2.5 hover:bg-[#2b2b2b] rounded-xl transition-all"
                    title="Copy number"
                  >
                    <Copy size={16} className="text-gray-400 hover:text-lime-400" />
                  </button>
                  <a
                    href={`tel:${selectedMember.Phone}`}
                    className="p-2.5 hover:bg-lime-400/20 rounded-xl transition-all"
                    title="Call"
                  >
                    <PhoneCall size={16} className="text-lime-400" />
                  </a>
                  <a
                    href={`https://wa.me/${selectedMember.Phone}`}
                    className="p-2.5 hover:bg-green-500/20 rounded-xl transition-all"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="WhatsApp"
                  >
                    <MessageCircle size={16} className="text-green-400" />
                  </a>
                </div>
              </div>

              {/* Joining Date – unchanged */}
              <div className="flex items-center gap-3 p-4 bg-[#222] rounded-xl">
                <div className="w-12 h-12 bg-blue-400/10 rounded-xl flex items-center justify-center">
                  <Calendar size={24} className="text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-400">Joined On</p>
                  <p className="text-lg font-semibold text-white">{formatToDDMMYYYY(selectedMember.joinDate) || 'N/A'}</p>
                </div>
              </div>

              {/* Plan Info – only add Expiry label */}
              <div className="p-6 bg-[#222]/50 rounded-xl space-y-3">
                <div className="flex items-center gap-2">
                  <Calendar size={18} className="text-gray-400" />
                  <span className="text-sm text-gray-400">Current Plan</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-white">
                    {selectedMember.PlanName || 'Basic'}
                  </span>

                  {/* expiry label + pill in a small flex row */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Expiry</span>
                    <span
                      className={`px-4 py-2 rounded-full text-sm font-semibold ${getExpiryStatus(selectedMember.ExpiryDate) === 'red'
                        ? 'bg-red-500/20 text-red-400 border-2 border-red-500/40'
                        : getExpiryStatus(selectedMember.ExpiryDate) === 'yellow'
                          ? 'bg-yellow-500/20 text-yellow-400 border-2 border-yellow-500/40'
                          : 'bg-lime-500/20 text-lime-400 border-2 border-lime-500/40'
                        }`}
                    >
                      {formatToDDMMYYYY(selectedMember.ExpiryDate) || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Plan History */}
              <div className="p-4 bg-[#222]/50 border border-[#2b2b2b] rounded-xl space-y-3">
                <button
                  type="button"
                  onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                  className="w-full flex items-center justify-between focus:outline-none"
                >
                  <div className="flex items-center gap-2">
                    <Calendar size={18} className="text-lime-400" />
                    <span className="text-sm font-medium text-white">
                      Plan History ({memberHistory.length > 0 ? (memberHistory.length === 1 ? 'First-time member' : `${memberHistory.length - 1} renewals`) : 'First-time member'})
                    </span>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`text-gray-400 transition-transform ${isHistoryExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {isHistoryExpanded && (
                  <div className="pt-2 space-y-3">
                    {isHistoryLoading ? (
                      <div className="text-gray-400 text-xs py-2 text-center animate-pulse">Loading history...</div>
                    ) : memberHistory.length === 0 ? (
                      <div className="text-gray-400 text-xs py-2 text-center">No history recorded yet.</div>
                    ) : (
                      <>
                        {/* If earliest entry start date doesn't match joined date, show note */}
                        {memberHistory.length > 0 && selectedMember.joinDate && (() => {
                          const earliest = memberHistory[memberHistory.length - 1];
                          const earliestStart = earliest.StartDate?.split('T')[0];
                          if (earliestStart && earliestStart !== selectedMember.joinDate) {
                            return (
                              <div className="text-[10px] text-yellow-400/80 bg-yellow-500/5 p-2 rounded-lg border border-yellow-500/10 mb-2">
                                ℹ️ History is available from {formatToDDMMYYYY(earliest.StartDate)} onward.
                              </div>
                            );
                          }
                          return null;
                        })()}

                        <div className="space-y-2.5 max-h-60 overflow-y-auto pr-1">
                          {(showAllHistory ? memberHistory : memberHistory.slice(0, 3)).map((hist, idx) => (
                            <div key={idx} className="pt-2.5 first:pt-0 first:border-t-0 border-t border-[#2b2b2b]">
                              <div className="flex justify-between items-start">
                                <div className="min-w-0 flex-1 pr-2">
                                  <p className="text-xs font-semibold text-white truncate">{hist.PlanName || 'Plan'}</p>
                                  <p className="text-[10px] text-gray-400">
                                    {hist.StartDate ? formatToDDMMYYYY(hist.StartDate) : 'N/A'} → {hist.ExpiryDate ? formatToDDMMYYYY(hist.ExpiryDate) : 'N/A'}
                                  </p>
                                  {hist.TrainerUsername && (
                                    <p className="text-[9px] text-gray-500 mt-0.5 truncate">
                                      Trainer: {hist.TrainerUsername}
                                    </p>
                                  )}
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className="text-xs font-semibold text-lime-400">₹{hist.Amount}</p>
                                  <p className="text-[9px] text-gray-500">
                                    {formatToDDMMYYYY(hist.TransactionDate)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {memberHistory.length > 3 && (
                          <div className="pt-2 text-center border-t border-[#2b2b2b]">
                            <button
                              type="button"
                              onClick={() => setShowAllHistory(!showAllHistory)}
                              className="text-[10px] font-semibold text-lime-400 hover:text-lime-300 focus:outline-none"
                            >
                              {showAllHistory ? 'Show Less' : `View All (${memberHistory.length})`}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="p-4 bg-[#1a1a1a]/50 rounded-xl">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-400/10 rounded-lg flex items-center justify-center">
                    <Users size={20} className="text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">Trainer Assignment</p>
                    <p className="text-xs text-gray-400">Assign trainer to this member</p>
                  </div>
                </div>

                {/* ✅ SHOW CURRENT TRAINER (if assigned) */}
                {selectedMember?.TrainerName && (
                  <div className="mb-3 p-3 bg-lime-500/10 border border-lime-500/30 rounded-lg">
                    <p className="text-xs text-lime-300 font-medium">
                      Currently assigned: <span className="font-semibold">{selectedMember.TrainerName}</span>
                    </p>
                  </div>
                )}

                {/* ✅ AUTO-SELECTED DROPDOWN */}
                <select
                  className="w-full bg-[#222] text-white border border-[#2b2b2b] rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  value={selectedMember?.TrainerId || ''}  // ✅ Uses embedded TrainerId
                  onChange={(e) => {
                    const newTrainerId = e.target.value === '' ? null : e.target.value;

                    // ✅ OPTIMISTIC UPDATE
                    setSelectedMember(prev => prev ? { ...prev, TrainerId: newTrainerId } : prev);

                    // ✅ API CALL with embedded data
                    updateMemberTrainer(selectedMember.username, newTrainerId);
                  }}
                >
                  <option value="">Unassigned</option>
                  {/* ✅ Create trainers list from ALL loaded members' trainer data */}
                  {[...new Set(members.map(m => m.TrainerId).filter(Boolean))].map(trainerId => {
                    const trainer = members.find(m => m.TrainerId === trainerId);
                    return (
                      <option key={trainerId} value={trainerId}>
                        {trainer?.TrainerName || 'Unknown Trainer'}
                      </option>
                    );
                  })}
                </select>
              </div>





            </div>

            {/* Footer buttons */}
            <div className="p-4 bg-[#1a1a1a] border-t border-[#2b2b2b] flex flex-col gap-2">
              {selectedMember.IsFrozen ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      handleManualUnfreeze(selectedMember.username);
                      setSelectedMember(null);
                    }}
                    className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 py-2 px-4 rounded-lg font-bold flex items-center justify-center gap-1.5 text-sm shadow-md hover:shadow-lg transition-all"
                  >
                    <Play size={16} />
                    Unfreeze
                  </button>
                  <button
                    onClick={() => {
                      confirmDeleteUser(selectedMember.username);
                      setSelectedMember(null);
                    }}
                    className="bg-red-500/90 hover:bg-red-500 text-white py-2 px-4 rounded-lg font-bold flex items-center justify-center gap-1.5 text-sm shadow-md hover:shadow-lg transition-all"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => {
                        setExtendMember(selectedMember);
                        setSelectedMember(null);
                      }}
                      className="bg-lime-400 hover:bg-lime-300 text-[#181f10] py-2 px-4 rounded-lg font-bold flex items-center justify-center gap-1.5 text-sm shadow-md hover:shadow-lg transition-all"
                    >
                      <Edit size={16} />
                      Extend
                    </button>
                    <button
                      onClick={() => {
                        setManualFreezeMember(selectedMember);
                        setShowManualFreezeModal(true);
                        setSelectedMember(null);
                      }}
                      className="bg-blue-500 hover:bg-blue-400 text-white py-2 px-4 rounded-lg font-bold flex items-center justify-center gap-1.5 text-sm shadow-md hover:shadow-lg transition-all"
                    >
                      <Clock size={16} />
                      Freeze
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      confirmDeleteUser(selectedMember.username);
                      setSelectedMember(null);
                    }}
                    className="w-full bg-red-500/90 hover:bg-red-500 text-white py-2 px-4 rounded-lg font-bold flex items-center justify-center gap-1.5 text-sm shadow-md hover:shadow-lg transition-all"
                  >
                    <Trash2 size={16} />
                    Delete Member
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}



      {/* Delete Confirmation */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => {
              setDeleteConfirmOpen(false);
              setUserToDelete(null);
            }}
          />
          <div className="relative z-10 w-full max-w-sm rounded-xl bg-[#181818] border border-[#2b2b2b] p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-white mb-2">Delete member?</h3>
            <p className="text-sm text-gray-300 mb-6">
              Are you sure you want to delete this member? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setUserToDelete(null);
                }}
                className="px-6 py-2.5 text-sm rounded-lg bg-[#222] text-gray-200 hover:bg-[#2b2b2b] transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => userToDelete && handleDelete(userToDelete)}
                className="px-6 py-2.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-400 transition-all shadow-lg"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddMember && (
        <AddMemberView
          onClose={() => setShowAddMember(false)}
          onAddMember={fetchGymUsers}
        />
      )}

      {extendMember && (
        <Extendplan
          member={extendMember}
          onClose={() => setExtendMember(null)}
          onAddMember={fetchGymUsers}
        />
      )}

      {showIDCard && idCardMember && (
        <IDCardModal
          member={idCardMember}
          onClose={() => {
            setShowIDCard(false);
            setIdCardMember(null);
          }}
          gymName={userInfo.gymname}
        />
      )}

      {showAddPay && selectedPayMember && (
        <RecordPaymentView
          onClose={() => {
            setShowAddPay(false);
            setSelectedPayMember(null);
          }}
          onRecordPayment={() => {
            fetchGymUsers();
            setShowAddPay(false);
            setSelectedPayMember(null);
          }}
          initialMemberId={selectedPayMember.username}
        />
      )}

      {showAddPlan && (
        <AddPlanView
          onClose={() => setShowAddPlan(false)}
          onAddMember={fetchGymUsers}
        />
      )}

      {/* Manual Freeze Modal */}
      {showManualFreezeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowManualFreezeModal(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative z-10 w-full max-w-sm rounded-2xl bg-[#181818] border border-[#2b2b2b] p-6 shadow-xl space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <Clock className="text-blue-400" size={20} />
              Manual Freeze: {manualFreezeMember?.Firstname} {manualFreezeMember?.Lastname}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-gray-400 text-[10px] uppercase tracking-widest font-bold mb-2">Duration (Days)</label>
                <input
                  type="number"
                  min="1"
                  value={manualFreezeDuration}
                  onChange={(e) => setManualFreezeDuration(Number(e.target.value))}
                  className="w-full px-4 py-2.5 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-xs text-white outline-none focus:border-[#6CD800]/50 transition-colors"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-[10px] uppercase tracking-widest font-bold mb-2">Reason</label>
                <textarea
                  rows={2}
                  placeholder="Enter manual freeze justification..."
                  value={manualFreezeReason}
                  onChange={(e) => setManualFreezeReason(e.target.value)}
                  className="w-full px-4 py-2.5 bg-[#0d0d0d] border border-[#2b2b2b] rounded-xl text-xs text-white outline-none focus:border-[#6CD800]/50 transition-colors resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => {
                  setShowManualFreezeModal(false);
                  setManualFreezeReason('');
                  setManualFreezeDuration(7);
                  setManualFreezeMember(null);
                }}
                className="px-4 py-2 rounded-lg bg-[#222] text-gray-200 text-xs hover:bg-[#2b2b2b] transition-all border border-[#2b2b2b]"
              >
                Cancel
              </button>
              <button
                onClick={handleManualFreeze}
                disabled={actioningFreeze || !manualFreezeDuration}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-semibold shadow-lg transition-all"
              >
                {actioningFreeze ? 'Freezing...' : 'Confirm Freeze'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersTab;
